import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Select } from '@99designs/common/components/Select';
import { FieldSet, Field } from 'common/components/Form';
import connectToStores from 'fluxible-addons-react/connectToStores';
import BookCalculatorAttributesStore from 'book-calculator/stores/BookCalculatorAttributesStore';
import BookCalculatorFieldOptionStore from 'book-calculator/stores/BookCalculatorFieldOptionStore';
import { handleUpdatedAttribute } from 'book-calculator/actions/handleUpdatedAttribute';
import GallerySlider from 'common/components/GallerySlider';

class InteriorFieldset extends React.Component {
    handleUpdateAttribute(key, value) {
        this.context.executeAction(handleUpdatedAttribute, {
            key: key,
            value: value,
        });
    }

    render() {
        const {
            interiorDesignComplexity,
            interiorDesignComplexityOptions,
            interiorDesignQuality,
        } = this.props;

        const galleryItems = this.getGalleryItems();
        const currentGalleryIndex = galleryItems.findIndex(i => i.value === interiorDesignQuality);

        return (
            <FieldSet twoColumn={false}>
                <div className="row">
                    <div className="column">
                        <Field
                            title={
                                <FormattedMessage id="fields.interior_design_complexity.label" />
                            }
                            variations={['jumbo-title']}
                        >
                            <Select
                                onChange={({ value }) => {
                                    this.handleUpdateAttribute('interior_design_complexity', value);
                                }}
                                name={'interior_design_complexity'}
                                value={interiorDesignComplexity}
                                variations={['short']}
                                options={interiorDesignComplexityOptions}
                            />
                        </Field>
                    </div>
                </div>
                <div className="row">
                    <div className="column">
                        <Field
                            title={<FormattedMessage id="fields.interior_design_quality.label" />}
                            variations={['jumbo-title']}
                        >
                            <GallerySlider
                                selectedItemIndex={currentGalleryIndex}
                                variations={['static']}
                                onChange={selectedItem => {
                                    this.handleUpdateAttribute(
                                        'interior_design_quality',
                                        selectedItem.value
                                    );
                                }}
                                items={galleryItems}
                            />
                        </Field>
                    </div>
                </div>

                {interiorDesignComplexity !== 'low' && this.renderImageFields()}
            </FieldSet>
        );
    }

    renderImageFields() {
        const {
            interiorDesignIllustrationCount,
            interiorDesignIllustrationCountOptions,
            interiorDesignPictureCount,
            interiorDesignPictureCountOptions,
        } = this.props;

        return (
            <div className="row">
                <div className="column small-12 medium-6 normal-6 large-6">
                    <Field
                        title={
                            <FormattedMessage id="fields.interior_design_illustration_count.label" />
                        }
                        variations={['jumbo-title']}
                    >
                        <Select
                            onChange={({ value }) => {
                                this.handleUpdateAttribute(
                                    'interior_design_illustration_count',
                                    parseInt(value, 10)
                                );
                            }}
                            name={'interior_design_illustration_count'}
                            value={interiorDesignIllustrationCount}
                            options={interiorDesignIllustrationCountOptions}
                        />
                    </Field>
                </div>
                <div className="column small-12 medium-6 normal-6 large-6">
                    <Field
                        title={<FormattedMessage id="fields.interior_design_picture_count.label" />}
                        variations={['jumbo-title']}
                    >
                        <Select
                            onChange={({ value }) => {
                                this.handleUpdateAttribute(
                                    'interior_design_picture_count',
                                    parseInt(value, 10)
                                );
                            }}
                            name={'interior_design_picture_count'}
                            value={interiorDesignPictureCount}
                            options={interiorDesignPictureCountOptions}
                        />
                    </Field>
                </div>
            </div>
        );
    }

    getGalleryItems() {
        const { interiorDesignComplexity, interiorDesignQualityOptions } = this.props;
        const imgSizeByComplexityMap = {
            low: { width: 839, height: 410 },
            medium: { width: 839, height: 243 },
            high: { width: 839, height: 306 },
        };

        return interiorDesignQualityOptions.map((item, index) => {
            const step = index + 1;
            const imgWidth = imgSizeByComplexityMap[interiorDesignComplexity].width;
            const imgHeight = imgSizeByComplexityMap[interiorDesignComplexity].height;
            const srcImg = require(`images/book-calculator/interior-${interiorDesignComplexity}-complexity-${step}.png`);

            return {
                value: item.value,
                topLabel: (
                    <div className="heading heading--h5 heading--no-margin heading--root">
                        {item.name}
                    </div>
                ),
                bottomLabel: (
                    <FormattedNumber
                        value={item['cost_by_complexity'][interiorDesignComplexity]}
                        style="currency"
                        currency={'USD'}
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                    />
                ),
                image: (
                    <img
                        width={imgWidth}
                        height={imgHeight}
                        src={`${srcImg}?auto=format&dpr=1&crop=faces&fit=crop&w=${imgWidth}&h=${imgHeight}`}
                        srcSet={`${srcImg}?auto=format&dpr=2&crop=faces&fit=crop&w=${imgWidth}&h=${imgHeight} 2x,
                            ${srcImg}?auto=format&dpr=2&crop=faces&fit=crop&w=${imgWidth}&h=${imgHeight} 3x`}
                    />
                ),
            };
        });
    }
}

InteriorFieldset.contextTypes = {
    executeAction: PropTypes.func.isRequired,
};

InteriorFieldset.propTypes = {
    interiorDesignComplexity: PropTypes.string.isRequired,
    interiorDesignComplexityOptions: PropTypes.array.isRequired,
    interiorDesignQuality: PropTypes.string.isRequired,
    interiorDesignQualityOptions: PropTypes.array.isRequired,
    interiorDesignIllustrationCount: PropTypes.number.isRequired,
    interiorDesignIllustrationCountOptions: PropTypes.array.isRequired,
    interiorDesignPictureCount: PropTypes.number.isRequired,
    interiorDesignPictureCountOptions: PropTypes.array.isRequired,
};

export default connectToStores(
    InteriorFieldset,
    [BookCalculatorAttributesStore],
    (context, prop) => {
        const attributes = context.getStore(BookCalculatorAttributesStore).getState();
        const options = context.getStore(BookCalculatorFieldOptionStore).getOptions();

        return {
            interiorDesignComplexity: attributes['interior_design_complexity'],
            interiorDesignComplexityOptions: options['interior_design_complexity'],
            interiorDesignQuality: attributes['interior_design_quality'],
            interiorDesignQualityOptions: options['interior_design_quality'],
            interiorDesignIllustrationCount: attributes['interior_design_illustration_count'],
            interiorDesignIllustrationCountOptions: options['interior_design_illustration_count'],
            interiorDesignPictureCount: attributes['interior_design_picture_count'],
            interiorDesignPictureCountOptions: options['interior_design_picture_count'],
        };
    }
);
